import React, { useState } from 'react';
import { useQuote } from '../context/QuoteContext';
import { SafeImage } from './SafeImage';
import { formatKsh } from '../utils/formatters';
import { X, Trash2, Plus, Minus, Send, CheckCircle2, ShoppingBag, ArrowRight } from 'lucide-react';

export const QuoteDrawer: React.FC = () => {
  const {
    items,
    removeFromQuote,
    updateQuantity,
    clearQuote,
    totalKsh,
    totalCount,
    isDrawerOpen,
    setIsDrawerOpen
  } = useQuote();

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [county, setCounty] = useState('Nairobi');
  const [notes, setNotes] = useState('');

  if (!isDrawerOpen) return null;

  const handleWhatsAppSend = () => {
    if (items.length === 0) return;
    const itemList = items
      .map(
        (i, idx) =>
          `${idx + 1}. ${i.product.name} (Qty: ${i.quantity}) - KSh ${formatKsh(
            i.product.priceKsh * i.quantity
          )}`
      )
      .join('%0A');

    const message = `Hello Saints Tech Solutions!%0A%0AI would like to request an official quotation for the following equipment:%0A%0A${itemList}%0A%0A*Total Estimated Value: KSh ${formatKsh(totalKsh)}*%0A%0AClient: ${customerName || 'Inquirer'}%0ABusiness: ${businessName || 'N/A'}%0ALocation: ${county}%0APhone: ${phoneNumber || 'Via WhatsApp'}%0A${notes ? `Notes: ${notes}` : ''}%0A%0APlease advise on availability, installation terms, and delivery timeline. Thank you!`;

    window.open(`https://wa.me/254727072393?text=${message}`, '_blank');
  };

  const handleSubmitFormalQuote = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    const newLead = {
      id: 'RFQ-' + Math.floor(100000 + Math.random() * 900000),
      type: 'quote',
      customerName,
      businessName: businessName || 'Commercial Operator',
      phone: phoneNumber,
      email: emailAddress,
      county,
      notes: notes || 'Direct storefront quotation request',
      items: items.map((i) => ({
        name: i.product.name,
        quantity: i.quantity,
        price: i.product.priceKsh,
      })),
      totalKsh,
      date: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
      status: 'New'
    };

    try {
      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newLead)
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Your quote request could not be sent. Please try again.');
      }
      setFormSubmitted(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Your quote request could not be sent. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormSubmitted(false);
    clearQuote();
    setIsDrawerOpen(false);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs transition-opacity"
        onClick={() => setIsDrawerOpen(false)}
      />

      <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
        <div className="w-screen max-w-md bg-white shadow-2xl flex flex-col">
          {/* Drawer Header */}
          <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between bg-slate-50">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center">
                <ShoppingBag className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900 leading-tight">
                  Quotation Basket
                </h3>
                <p className="text-xs text-slate-500">
                  {totalCount} {totalCount === 1 ? 'item' : 'items'} selected
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsDrawerOpen(false)}
              className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200/60 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Drawer Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {formSubmitted ? (
              <div className="py-12 text-center space-y-4">
                <div className="w-14 h-14 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h4 className="text-xl font-bold text-slate-900">
                  Quotation Request Received!
                </h4>
                <p className="text-sm text-slate-600 leading-relaxed max-w-xs mx-auto">
                  Thank you, <span className="font-semibold text-slate-800">{customerName}</span>. Our technical sales engineering team will prepare your formal Proforma Invoice and call you at <span className="font-semibold text-slate-800">{phoneNumber}</span> within 2 business hours.
                </p>
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-600 text-left space-y-1">
                  <div><span className="font-medium text-slate-800">Delivery Target:</span> {county}, Kenya</div>
                  <div><span className="font-medium text-slate-800">Items:</span> {totalCount} units</div>
                  <div><span className="font-medium text-slate-800">Estimated Total:</span> KSh {formatKsh(totalKsh)}</div>
                </div>
                <div className="pt-4 space-y-2">
                  <button
                    onClick={handleWhatsAppSend}
                    className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg flex items-center justify-center gap-2 cursor-pointer transition-colors"
                  >
                    <span>Also Send via WhatsApp</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleReset}
                    className="w-full py-2 px-4 text-xs text-slate-600 hover:text-slate-900 font-medium"
                  >
                    Close & Start New Inquiry
                  </button>
                </div>
              </div>
            ) : items.length === 0 ? (
              <div className="py-16 text-center space-y-3">
                <div className="w-12 h-12 bg-slate-100 text-slate-400 rounded-full flex items-center justify-center mx-auto">
                  <ShoppingBag className="w-6 h-6" />
                </div>
                <p className="text-slate-600 text-sm font-medium">Your quote basket is currently empty.</p>
                <p className="text-xs text-slate-400 max-w-xs mx-auto">
                  Browse our Water Purification Systems, Milk ATMs, or Vending Stations to add equipment to your estimate.
                </p>
              </div>
            ) : (
              <>
                {/* List of items */}
                <div className="space-y-3">
                  {items.map(({ product, quantity }) => (
                    <div
                      key={product.id}
                      className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 flex items-start gap-3"
                    >
                      <SafeImage
                        src={product.image}
                        fallbackCategory={product.category}
                        alt={product.name}
                        className="w-16 h-16 rounded-lg object-cover bg-slate-200 shrink-0 border border-slate-200"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="text-xs font-semibold text-slate-900 leading-snug line-clamp-2">
                          {product.name}
                        </h4>
                        <p className="text-xs text-sky-700 font-bold tabular-nums mt-0.5">
                          KSh {formatKsh(product.priceKsh)}
                        </p>

                        <div className="flex items-center justify-between mt-2.5">
                          <div className="flex items-center gap-1.5 bg-white border border-slate-300 rounded-md p-0.5">
                            <button
                              onClick={() => updateQuantity(product.id, quantity - 1)}
                              className="p-1 text-slate-500 hover:text-slate-800 rounded hover:bg-slate-100"
                              title="Decrease quantity"
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="text-xs font-semibold px-2 tabular-nums">
                              {quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(product.id, quantity + 1)}
                              className="p-1 text-slate-500 hover:text-slate-800 rounded hover:bg-slate-100"
                              title="Increase quantity"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>

                          <button
                            onClick={() => removeFromQuote(product.id)}
                            className="p-1 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded"
                            title="Remove item"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Subtotal summary */}
                <div className="p-4 bg-sky-50/70 border border-sky-100 rounded-xl space-y-2">
                  <div className="flex justify-between text-xs text-slate-600">
                    <span>Selected Equipment</span>
                    <span className="tabular-nums font-medium">{totalCount} Units</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-600">
                    <span>Warranty & Commissioning</span>
                    <span className="text-emerald-700 font-medium">Included</span>
                  </div>
                  <div className="border-t border-sky-200/60 pt-2 flex justify-between items-baseline">
                    <span className="text-sm font-bold text-slate-900">Total Estimate</span>
                    <span className="text-lg font-extrabold text-sky-800 tabular-nums">
                      KSh {formatKsh(totalKsh)}
                    </span>
                  </div>
                </div>

                {/* RFQ Form */}
                <form onSubmit={handleSubmitFormalQuote} className="space-y-3 pt-2">
                  <h4 className="text-xs uppercase tracking-wider text-slate-500 font-bold">
                    Official Proforma Details
                  </h4>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Your Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="e.g. John Mwangi"
                      className="w-full text-xs px-3 py-2 bg-white border border-slate-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">
                        Phone / M-Pesa *
                      </label>
                      <input
                        type="tel"
                        required
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder="07XX XXX XXX"
                        className="w-full text-xs px-3 py-2 bg-white border border-slate-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">
                        Delivery County
                      </label>
                      <select
                        value={county}
                        onChange={(e) => setCounty(e.target.value)}
                        className="w-full text-xs px-2.5 py-2 bg-white border border-slate-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                      >
                        <option value="Nairobi">Nairobi</option>
                        <option value="Kiambu">Kiambu</option>
                        <option value="Machakos">Machakos</option>
                        <option value="Nakuru">Nakuru</option>
                        <option value="Uasin Gishu / Eldoret">Eldoret</option>
                        <option value="Kisumu">Kisumu</option>
                        <option value="Mombasa">Mombasa</option>
                        <option value="Nyeri / Mt Kenya">Nyeri</option>
                        <option value="Meru">Meru</option>
                        <option value="Other County">Other County</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Business or Cooperative Name (Optional)
                    </label>
                    <input
                      type="text"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      placeholder="e.g. Highlands Fresh Dairy"
                      className="w-full text-xs px-3 py-2 bg-white border border-slate-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={emailAddress}
                      onChange={(e) => setEmailAddress(e.target.value)}
                      placeholder="name@business.co.ke"
                      className="w-full text-xs px-3 py-2 bg-white border border-slate-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                    />
                  </div>

                  {submitError && (
                    <p role="alert" className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      {submitError}
                    </p>
                  )}

                  <div className="pt-2 space-y-2">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-2.5 px-4 bg-sky-600 hover:bg-sky-700 text-white text-xs font-bold rounded-lg shadow-sm transition-colors cursor-pointer flex items-center justify-center gap-1.5"
                    >
                      <Send className="w-3.5 h-3.5" />
                      {isSubmitting ? 'Sending Request...' : 'Submit Formal RFQ Request'}
                    </button>

                    <button
                      type="button"
                      onClick={handleWhatsAppSend}
                      className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-sm transition-colors cursor-pointer flex items-center justify-center gap-1.5"
                    >
                      <Send className="w-3.5 h-3.5" />
                      Instant Quote via WhatsApp (+254 727 072 393)
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
